import { cn } from '@/shared/utilities';
import { memo, useEffect, useRef, useState } from 'react';
import { Blurhash } from 'react-blurhash';
import { PhotoView } from 'react-photo-view';
import { useAttachmentUrl } from '../../queries/use-attachment-url';

interface ImageViewProps {
  path: string;
  fallbackUrl?: string;
  alt: string;
  blurHash?: Nullable<string>;
  width?: number;
  height?: number;
  objectFit?: 'cover' | 'contain' | 'fill' | 'none' | 'scale-down';
  isExtreme?: boolean;
}

const ImageView: React.FC<ImageViewProps> = ({
  path,
  fallbackUrl,
  blurHash,
  alt,
  objectFit = 'cover',
  isExtreme = false
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const imgRef = useRef<HTMLImageElement>(null);

  const { blobUrl, isError: hasError } = useAttachmentUrl(path);

  // Determine which URL to use
  const imageUrl = blobUrl || fallbackUrl;
  const shouldShowImage = imageUrl && !imageError;

  // Reset state when URL changes
  useEffect(() => {
    setImageLoaded(false);
    setImageError(false);
  }, [imageUrl]);

  // Handle image load
  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  // Handle image error
  const handleImageError = () => {
    setImageError(true);
    setImageLoaded(false);
  };

  // Preload image for smoother transition
  useEffect(() => {
    if (!imageUrl) return;

    const img = new Image();
    img.src = imageUrl;
    img.onload = () => setImageLoaded(true);
    img.onerror = () => setImageError(true);

    return () => {
      img.onload = null;
      img.onerror = null;
    };
  }, [imageUrl]);

  const showError = hasError || imageError;

  return (
    <div
      className={cn('mex:relative mex:inset-0 mex:h-full mex:w-full mex:overflow-hidden', {
        'mex:min-w-[200px]': showError,
        'mex:bg-gray-200': isExtreme && objectFit === 'contain'
      })}
    >
      {/* Blurhash background - always show until image loads */}
      <div
        className={cn(
          'mex:absolute mex:inset-0 mex:z-0 mex:flex mex:h-full mex:w-full mex:items-center mex:justify-center mex:transition-opacity mex:duration-300',
          {
            'mex:opacity-0': imageLoaded,
            'mex:opacity-100': !imageLoaded
          }
        )}
        role='img'
        aria-label={imageLoaded ? undefined : 'Đang tải ảnh...'}
      >
        {blurHash ? (
          <Blurhash
            hash={blurHash}
            width='100%'
            height='100%'
            resolutionX={32}
            resolutionY={32}
            punch={1}
            className='h-full mex:w-full'
            style={{ objectFit }}
          />
        ) : (
          <div className='h-full mex:w-full bg-gray-100' />
        )}
      </div>

      {/* Actual image */}
      {shouldShowImage && (
        <PhotoView src={imageUrl}>
          <img
            ref={imgRef}
            src={imageUrl}
            alt={alt || 'Hình ảnh đính kèm'}
            loading='lazy'
            decoding='async'
            onLoad={handleImageLoad}
            onError={handleImageError}
            className={cn(
              'mex:relative mex:z-10 mex:h-full mex:w-full mex:cursor-pointer mex:transition-opacity mex:duration-300 mex:ease-in-out',
              {
                'mex:opacity-100': imageLoaded,
                'mex:opacity-0': !imageLoaded
              }
            )}
            style={{ objectFit }}
          />
        </PhotoView>
      )}
    </div>
  );
};

// Custom comparison function for better memoization
const areEqual = (prevProps: ImageViewProps, nextProps: ImageViewProps) => {
  return (
    prevProps.path === nextProps.path &&
    prevProps.fallbackUrl === nextProps.fallbackUrl &&
    prevProps.blurHash === nextProps.blurHash &&
    prevProps.alt === nextProps.alt &&
    prevProps.objectFit === nextProps.objectFit &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.isExtreme === nextProps.isExtreme
  );
};

export default memo(ImageView, areEqual);
